import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Loader2, User, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Business form state — colors are configured per-card on the Cards page,
  // not at the merchant level (kept out of the Settings UI to avoid confusion).
  const [biz, setBiz] = useState({
    business_name: '',
    address: '',
    phone: '',
    siret: '',
  });

  // Profile form state
  const [profile, setProfile] = useState({
    email: '',
    name: '',
    old_password: '',
    new_password: '',
  });

  useEffect(() => {
    if (user) {
      setBiz({
        business_name: user.business_name || '',
        address: user.address || '',
        phone: user.phone || '',
        siret: user.siret || '',
      });
      setProfile({
        email: user.email || '',
        name: user.name || '',
        old_password: '',
        new_password: '',
      });
    }
  }, [user]);

  const saveBusiness = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send only non-empty fields
      const payload = Object.fromEntries(
        Object.entries(biz).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      await axios.patch(`${API_URL}/api/merchant/business`, payload, { withCredentials: true });
      toast.success('Informations entreprise mises à jour');
      if (refreshUser) await refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Erreur lors de la sauvegarde';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(err.response?.data?.errors)) {
        msg = err.response.data.errors.map((e) => e.message || e).join('. ');
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      if (profile.name && profile.name !== user.name) payload.name = profile.name;
      if (profile.email && profile.email !== user.email) payload.email = profile.email;
      if (profile.new_password) {
        if (!profile.old_password) {
          toast.error('Mot de passe actuel requis');
          setLoading(false);
          return;
        }
        payload.old_password = profile.old_password;
        payload.new_password = profile.new_password;
      }
      if (Object.keys(payload).length === 0) {
        toast.info('Aucun changement à enregistrer');
        setLoading(false);
        return;
      }
      await axios.patch(`${API_URL}/api/auth/profile`, payload, { withCredentials: true });
      toast.success('Profil mis à jour');
      setProfile({ ...profile, old_password: '', new_password: '' });
      if (refreshUser) await refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="settings-title">Paramètres</h1>
        <p className="text-slate-500 mt-1">Gère ton profil et les informations de ton entreprise.</p>
      </div>

      {/* Business section */}
      <Card data-testid="settings-business-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <CardTitle>Mon entreprise</CardTitle>
          </div>
          <CardDescription>Ces informations apparaissent sur les cartes Wallet de tes clients.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveBusiness} className="space-y-4">
            <div>
              <Label htmlFor="business_name">Nom de l'entreprise *</Label>
              <Input
                id="business_name"
                data-testid="biz-name-input"
                value={biz.business_name}
                onChange={(e) => setBiz({ ...biz, business_name: e.target.value })}
                placeholder="Mestora Café"
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                data-testid="biz-address-input"
                value={biz.address}
                onChange={(e) => setBiz({ ...biz, address: e.target.value })}
                placeholder="12 rue de la Paix, 75001 Paris"
                maxLength={200}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  data-testid="biz-phone-input"
                  value={biz.phone}
                  onChange={(e) => setBiz({ ...biz, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              <div>
                <Label htmlFor="siret">SIRET</Label>
                <Input
                  id="siret"
                  data-testid="biz-siret-input"
                  value={biz.siret}
                  onChange={(e) => setBiz({ ...biz, siret: e.target.value })}
                  placeholder="12345678901234"
                  pattern="[0-9]{14}"
                  maxLength={14}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              Les couleurs de marque se configurent <strong>sur chaque carte</strong> dans
              "Mes cartes" → Modifier → onglet Apparence.
            </p>
            <Button type="submit" disabled={loading} data-testid="save-business-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Profile section */}
      <Card data-testid="settings-profile-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            <CardTitle>Mon profil</CardTitle>
          </div>
          <CardDescription>Identifiants de connexion à ton compte LoyalPass.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4">
            <div>
              <Label htmlFor="profile_name">Nom du propriétaire</Label>
              <Input
                id="profile_name"
                data-testid="profile-name-input"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Jean Dupont"
                maxLength={100}
              />
            </div>
            <div>
              <Label htmlFor="profile_email">Email</Label>
              <Input
                id="profile_email"
                data-testid="profile-email-input"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="jean@mestora.fr"
              />
              {user && user.email_verified === false && (
                <p className="text-xs text-amber-600 mt-1" data-testid="email-unverified-banner">
                  Email non confirmé. Vérifie ta boîte mail.
                </p>
              )}
            </div>
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700 mb-2">Changer le mot de passe (optionnel)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="old_password">Mot de passe actuel</Label>
                  <Input
                    id="old_password"
                    data-testid="profile-old-password-input"
                    type="password"
                    value={profile.old_password}
                    onChange={(e) => setProfile({ ...profile, old_password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <Input
                    id="new_password"
                    data-testid="profile-new-password-input"
                    type="password"
                    value={profile.new_password}
                    onChange={(e) => setProfile({ ...profile, new_password: e.target.value })}
                    placeholder="Minimum 8 caractères"
                    minLength={8}
                  />
                </div>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="save-profile-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
